import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from './Navbar';
import MysticalParticles from './MysticalParticles';
import { Shield, Check, X, Star, Eye, AlertTriangle, Users, Coins } from 'lucide-react';

const AdminModeration = () => {
  const [pendingSubmissions, setPendingSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('mystveil_token'));
  const [moderating, setModerating] = useState(null);
  const [moderationNotes, setModerationNotes] = useState('');

  useEffect(() => {
    const loadPendingSubmissions = async () => {
      if (!token) {
        setError('Please log in to access moderation');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        const response = await fetch('https://api.4fingerstudios.com/api/mystveil/admin/pending-submissions', {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (!response.ok) {
          if (response.status === 401) {
            setError('Session expired. Please log in again.');
            localStorage.removeItem('mystveil_token');
            setToken(null);
          } else if (response.status === 403) {
            setError('Access denied. Admin role required.');
          } else {
            throw new Error(`Failed to load submissions: ${response.status}`);
          }
          return;
        }

        const data = await response.json();
        setPendingSubmissions(data || []);
      } catch (err) {
        setError('Failed to load pending submissions');
        console.error('Submissions load error:', err);
      } finally {
        setLoading(false);
      }
    };

    loadPendingSubmissions();
  }, [token]);

  const handleModeration = async (submissionId, status) => {
    setModerating(submissionId);
    try {
      const response = await fetch('https://api.4fingerstudios.com/api/mystveil/admin/moderate-image', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          imageSubmissionId: submissionId,
          status: status,
          moderatorNotes: moderationNotes
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || `Failed to moderate submission: ${response.status}`);
      }

      // Remove the moderated submission from the list
      setPendingSubmissions(prev => prev.filter(s => s.id !== submissionId));
      setModerationNotes('');
    } catch (err) {
      alert(`Failed to moderate submission: ${err.message}`);
    } finally {
      setModerating(null);
    }
  };

  const handleFeature = async (submissionId) => {
    try {
      const response = await fetch('https://api.4fingerstudios.com/api/mystveil/admin/feature-image', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          imageSubmissionId: submissionId
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || `Failed to feature submission: ${response.status}`);
      }

      alert('Image featured successfully!');
    } catch (err) {
      alert(`Failed to feature submission: ${err.message}`);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen flex flex-col relative">
        <div className="fixed inset-0 -z-10">
          <img 
            src="https://cdn.4fingerstudios.com/Mystveil/Mystveil_map.png" 
            alt="Mystveil Township Map" 
            className="w-full h-full object-cover opacity-30 blur-sm" 
          />
          <div className="absolute inset-0 bg-gradient-to-br from-purple-900/20 via-pink-900/20 to-purple-900/20"></div>
        </div>
        <MysticalParticles />
        <Navbar />
        
        <main className="flex-grow flex items-center justify-center px-4 py-8">
          <div className="mystical-card max-w-md mx-auto text-center">
            <h1 className="mystical-title mb-6">Admin Moderation</h1>
            <p className="mystical-text mb-6">Please log in to access moderation</p>
            <Link to="/auth/login" className="mystical-button">
              Login
            </Link>
          </div>
        </main>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col relative">
        <div className="fixed inset-0 -z-10">
          <img 
            src="https://cdn.4fingerstudios.com/Mystveil/Mystveil_map.png" 
            alt="Mystveil Township Map" 
            className="w-full h-full object-cover opacity-30 blur-sm" 
          />
          <div className="absolute inset-0 bg-gradient-to-br from-purple-900/20 via-pink-900/20 to-purple-900/20"></div>
        </div>
        <MysticalParticles />
        <Navbar />
        
        <main className="flex-grow flex items-center justify-center px-4 py-8">
          <div className="mystical-card">
            <p className="mystical-text">Loading pending submissions...</p>
          </div>
        </main>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex flex-col relative">
        <div className="fixed inset-0 -z-10">
          <img 
            src="https://cdn.4fingerstudios.com/Mystveil/Mystveil_map.png" 
            alt="Mystveil Township Map" 
            className="w-full h-full object-cover opacity-30 blur-sm" 
          />
          <div className="absolute inset-0 bg-gradient-to-br from-purple-900/20 via-pink-900/20 to-purple-900/20"></div>
        </div>
        <MysticalParticles />
        <Navbar />
        
        <main className="flex-grow flex items-center justify-center px-4 py-8">
          <div className="mystical-card max-w-md mx-auto text-center">
            <h1 className="mystical-title mb-6">Error</h1>
            <p className="mystical-text mb-6 text-red-600">{error}</p>
            <Link to="/" className="mystical-button">
              Return Home
            </Link>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col relative">
      {/* Magical Background */}
      <div className="fixed inset-0 -z-10">
        <img 
          src="https://cdn.4fingerstudios.com/Mystveil/Mystveil_map.png" 
          alt="Mystveil Township Map" 
          className="w-full h-full object-cover opacity-30 blur-sm" 
        />
        <div className="absolute inset-0 bg-gradient-to-br from-purple-900/20 via-pink-900/20 to-purple-900/20"></div>
      </div>

      {/* Magical Particles */}
      <MysticalParticles />

      {/* Navigation */}
      <Navbar />

      {/* Main Content */}
      <main className="flex-grow px-4 py-8">
        <div className="max-w-6xl mx-auto">
          <div className="mystical-card mb-8">
            <div className="flex items-center space-x-3 mb-6">
              <Shield className="text-purple-800" size={32} />
              <h1 className="mystical-title text-black">
                Admin Moderation
              </h1>
            </div>
            
            <div className="mystical-card bg-white bg-opacity-95 mb-6">
              <p className="mystical-text text-black">
                Review and moderate image submissions from the community. Approve submissions to award chips, 
                or reject them with feedback. You can also feature exceptional artwork.
              </p>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
              <div className="mystical-card bg-white bg-opacity-95 text-center">
                <h3 className="mystical-subtitle text-black mb-2">Pending Review</h3>
                <p className="mystical-text text-3xl font-bold text-yellow-600">
                  {pendingSubmissions.length}
                </p>
              </div>
              <div className="mystical-card bg-white bg-opacity-95 text-center">
                <h3 className="mystical-subtitle text-black mb-2">Total Chips Pending</h3>
                <p className="mystical-text text-3xl font-bold text-purple-800">
                  {pendingSubmissions.reduce((sum, s) => sum + (s.totalChips || 0), 0)}
                </p>
              </div>
              <div className="mystical-card bg-white bg-opacity-95 text-center">
                <h3 className="mystical-subtitle text-black mb-2">Unique Artists</h3>
                <p className="mystical-text text-3xl font-bold text-green-600">
                  {new Set(pendingSubmissions.map(s => s.username)).size}
                </p>
              </div>
            </div>
          </div>

          {/* Pending Submissions */}
          <div className="mystical-card">
            <h3 className="mystical-subtitle text-black text-xl mb-6">
              Pending Submissions
            </h3>
            
            {pendingSubmissions.length > 0 ? (
              <div className="space-y-6">
                {pendingSubmissions.map(submission => (
                  <div key={submission.id} className="mystical-card bg-white bg-opacity-95">
                    <div className="flex items-start space-x-6">
                      {/* Image */}
                      <div className="flex-shrink-0">
                        <img 
                          src={submission.imageUrl} 
                          alt={submission.imageTitle}
                          className="w-32 h-32 rounded-md object-cover"
                        />
                      </div>
                      
                      {/* Submission Details */}
                      <div className="flex-1">
                        <div className="flex items-start justify-between mb-4">
                          <div>
                            <h4 className="mystical-subtitle text-black text-lg mb-2">
                              {submission.imageTitle || 'Untitled'}
                            </h4>
                            <p className="mystical-text text-gray-600 mb-2">
                              by <strong>{submission.username}</strong>
                            </p>
                            <p className="mystical-text text-gray-600 text-sm">
                              {submission.imageDescription || 'No description provided'}
                            </p>
                          </div>
                          <div className="text-right">
                            <div className="flex items-center space-x-2 mb-2">
                              <Coins className="text-yellow-600" size={16} />
                              <span className="mystical-text font-bold text-purple-800">
                                {submission.totalChips} chips
                              </span>
                            </div>
                            <p className="mystical-text text-sm text-gray-500">
                              Submitted: {new Date(submission.submittedAt).toLocaleDateString()}
                            </p>
                          </div>
                        </div>
                        
                        {/* Submission Info */}
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                          <div>
                            <p className="mystical-text text-sm text-gray-500">Art Type</p>
                            <p className="mystical-text font-bold">{submission.artType}</p>
                          </div>
                          <div>
                            <p className="mystical-text text-sm text-gray-500">Characters</p>
                            <p className="mystical-text font-bold">{submission.characterCount}</p>
                          </div>
                          <div>
                            <p className="mystical-text text-sm text-gray-500">Base Chips</p>
                            <p className="mystical-text font-bold">{submission.baseChips}</p>
                          </div>
                          <div>
                            <p className="mystical-text text-sm text-gray-500">Bonus Chips</p>
                            <p className="mystical-text font-bold">{submission.bonusChips}</p>
                          </div>
                        </div>
                        
                        {/* Moderation Notes */}
                        <div className="mb-4">
                          <label className="block mystical-text text-black font-bold mb-2">
                            Moderation Notes
                          </label>
                          <textarea
                            value={moderationNotes}
                            onChange={(e) => setModerationNotes(e.target.value)}
                            className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-700 focus:border-transparent"
                            rows="2"
                            placeholder="Add notes for the artist (optional)"
                          />
                        </div>
                        
                        {/* Action Buttons */}
                        <div className="flex flex-wrap gap-3">
                          <button
                            onClick={() => handleModeration(submission.id, 'approved')}
                            disabled={moderating === submission.id}
                            className="mystical-button bg-green-600 hover:bg-green-700"
                          >
                            {moderating === submission.id ? (
                              <>
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                                Processing...
                              </>
                            ) : (
                              <>
                                <Check className="mr-2" size={16} />
                                Approve
                              </>
                            )}
                          </button>
                          
                          <button
                            onClick={() => handleModeration(submission.id, 'rejected')}
                            disabled={moderating === submission.id}
                            className="mystical-button bg-red-600 hover:bg-red-700"
                          >
                            <X className="mr-2" size={16} />
                            Reject
                          </button>
                          
                          <button
                            onClick={() => handleFeature(submission.id)}
                            className="mystical-button bg-yellow-600 hover:bg-yellow-700"
                          >
                            <Star className="mr-2" size={16} />
                            Feature
                          </button>
                          
                          <a
                            href={submission.imageUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="mystical-button bg-gray-600 hover:bg-gray-700"
                          >
                            <Eye className="mr-2" size={16} />
                            View Full Size
                          </a>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="mystical-card bg-white bg-opacity-95 text-center">
                <AlertTriangle className="mx-auto text-gray-400 mb-4" size={64} />
                <h3 className="mystical-subtitle text-black text-xl mb-4">
                  No Pending Submissions
                </h3>
                <p className="mystical-text text-gray-600 mb-6">
                  All submissions have been reviewed. Great job keeping up with the moderation queue!
                </p>
                <Link to="/gallery" className="mystical-button">
                  View Gallery
                </Link>
              </div>
            )}
          </div>

          {/* Admin Tools */}
          <div className="mystical-card mt-8">
            <h3 className="mystical-subtitle text-black text-xl mb-6">
              Admin Tools
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Link to="/mystveil/admin/grant-chips" className="mystical-button">
                <Coins className="mr-2" size={20} />
                Grant Chips to User
              </Link>
              <Link to="/gallery" className="mystical-button">
                <Eye className="mr-2" size={20} />
                View Gallery
              </Link>
            </div>
          </div>

          {/* Navigation Buttons */}
          <div className="flex flex-wrap justify-center gap-4 mt-8">
            <Link to="/profile" className="mystical-button">
              My Profile
            </Link>
            <Link to="/gallery" className="mystical-button">
              View Gallery
            </Link>
            <Link to="/" className="mystical-button">
              Return Home
            </Link>
          </div>
        </div>
      </main>

      {/* Mystical Footer */}
      <footer className="mystical-nav py-6 mt-auto">
        <div className="container mx-auto px-4 text-center">
          <p className="mystical-text font-bold">
            © 2025 4fingerstudios <br /> 
            © 2024 Reckless Dreams. All Rights Reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default AdminModeration;
